/**
 * StatTile — one tile in a KPI strip (list page) or a stat strip (detail
 * page). Shape is identical between the two: 3px tone-coloured stripe down
 * the left, label, 26×26 rounded-square icon chip, big value, meta line.
 *
 * Used by `<KpiStrip>` (list-side, four tiles in a row) and the detail
 * page's stat strip. Lifted to `master-list` so customer + vendor +
 * future verticals share one tile shape.
 *
 * @see prototype/sales/customers.html `.kpi`
 * @see prototype/purchases/vendor-detail.html `.stat-tile`
 */
import { cn } from "@flint/ui";
import type { LucideIcon } from "lucide-react";

export type Tone = "sky" | "ok" | "warn" | "bad" | "muted";

const TONE_STYLES: Record<Tone, { stripe: string; iconBg: string; iconFg: string }> = {
  sky: { stripe: "bg-sky-600", iconBg: "bg-sky-50", iconFg: "text-sky-700" },
  ok: { stripe: "bg-ok", iconBg: "bg-ok-50", iconFg: "text-ok" },
  warn: { stripe: "bg-warn", iconBg: "bg-warn-50", iconFg: "text-warn" },
  bad: { stripe: "bg-bad", iconBg: "bg-bad-50", iconFg: "text-bad" },
  muted: { stripe: "bg-line-2", iconBg: "bg-raised", iconFg: "text-muted" },
};

export interface StatTileProps {
  label: string;
  /** Big number / metric. Pass `'—'` for placeholder tiles. */
  value: string;
  /** Sub-line under the value (e.g. "wires up with invoices"). */
  meta: string;
  tone: Tone;
  icon: LucideIcon;
  /** When true, renders the value muted (placeholder shape). */
  placeholder?: boolean | undefined;
}

export function StatTile({ label, value, meta, tone, icon: Icon, placeholder }: StatTileProps) {
  const styles = TONE_STYLES[tone];
  return (
    <div className="relative overflow-hidden bg-paper border border-line rounded-md shadow-sm hover:border-line-2 transition-colors">
      {/* 3px tone-coloured stripe — prototype `.kpi-stripe` / `.stat-stripe`. */}
      <span
        aria-hidden
        className={cn("absolute left-0 top-0 bottom-0 w-[3px] rounded-l-md", styles.stripe)}
      />
      <div className="px-4 py-3.5 pl-5">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <p className="text-xs text-muted">{label}</p>
          <span
            aria-hidden
            className={cn(
              "inline-flex h-[26px] w-[26px] items-center justify-center rounded-md shrink-0",
              styles.iconBg,
              styles.iconFg,
            )}
          >
            <Icon size={14} strokeWidth={2} />
          </span>
        </div>
        <p
          className={cn(
            "text-2xl leading-none num tracking-tight",
            placeholder ? "text-muted" : "font-bold text-ink",
          )}
        >
          {value}
        </p>
        <p className="text-xs text-muted mt-1.5 truncate" title={meta}>
          {meta}
        </p>
      </div>
    </div>
  );
}
